import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { useAtomsToastManager } from '@opendoor/atoms/toast';
import { IconCheck } from '@tabler/icons-react';
import type { SavedSearchFieldsFragment } from '__generated__/athena/graphql';

import { useObservability } from '../../../helpers/observability';
import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import {
  findMatchingSavedSearch,
  hasValidLocation,
  queryMatchesSavedSearch,
  toSavedSearchInput,
} from './mapping';
import { claimPendingSavedSearch, stashPendingSavedSearch } from './pendingSavedSearch';
import { formatSavedSearchName } from './presentation';
import { NameSavedSearchDialog, UpdateSavedSearchDialog } from './SaveSearchDialogs';
import { useSavedSearches } from './useSavedSearches';

export interface SaveSearchButtonProps {
  /**
   * The canonical filter shape for the search being saved. The parent must
   * merge `searchState.getCanonical()` with the active location (market,
   * zoneIds, or polygon vertices) before passing it in; `getCanonical()`
   * intentionally omits location.
   */
  query: PropertySearchQuery;
  /**
   * The saved search the current results came from, if any. Drives the
   * update-vs-save-new flow: while the page state still matches it the button
   * shows a quiet "Saved" state, and once it diverges the button offers to
   * update it in place instead of piling up near-duplicates.
   */
  activeSavedSearch?: SavedSearchFieldsFragment | null;
  /**
   * Display label for the active location (e.g. a selected neighborhood name),
   * used to build a better default name than the market slug.
   */
  locationLabel?: string | null;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  onSaved?: (savedSearchId: string) => void;
}

const NO_LOCATION_HINT = 'Pick a market, neighborhood, or area on the map first.';
const GENERIC_SAVE_ERROR = 'Failed to save search. Please try again.';
const GENERIC_UPDATE_ERROR = 'Failed to update search. Please try again.';
const LIST_LOAD_ERROR = 'Couldn’t load your saved searches. Please try again.';

// The marketplace doesn't have an in-page login modal, so unauthenticated
// users are bounced to `/login?redirectTo=...` matching `usePropertyReactions`.
// The pending search is stashed first so the save resumes after login.
const redirectToLogin = () => {
  const redirectTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
};

// 'closed' — no dialog.
// 'name' — the name-and-save dialog (new search).
// 'choose' — active search is dirty: offer update-in-place vs save-as-new.
type DialogMode = 'closed' | 'name' | 'choose';

export const SaveSearchButton = ({
  query,
  activeSavedSearch,
  locationLabel,
  variant = 'secondary',
  size = 'lg',
  onSaved,
}: SaveSearchButtonProps) => {
  const { isLoggedIn, savedSearches, hasLoaded, add, update, refetch } = useSavedSearches();
  const { trackEvent } = useObservability();
  const toastManager = useAtomsToastManager();
  // `useAtomsToastManager` returns a fresh object each render, so read it
  // through a ref instead of a dependency (matches CopyLinkToast.tsx et al.).
  const toastManagerRef = useRef(toastManager);
  toastManagerRef.current = toastManager;

  const [mode, setMode] = useState<DialogMode>('closed');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Set when submit found an equivalent existing search; the next submit
  // ("Save anyway") proceeds past the warning.
  const [duplicateOf, setDuplicateOf] = useState<SavedSearchFieldsFragment | null>(null);
  // A save resumed after the login bounce uses the query stashed pre-login,
  // not the (possibly SSR-reset) live page query.
  const [resumedQuery, setResumedQuery] = useState<PropertySearchQuery | null>(null);
  // Used to drive focus into the name field once the Dialog portal mounts.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
  }, []);
  const nameInputId = useId();

  const validLocation = hasValidLocation(query);
  const isDirty = !!activeSavedSearch && !queryMatchesSavedSearch(query, activeSavedSearch);
  // Only treat the page as cleanly matching once the list has loaded; otherwise
  // an empty pre-load store would falsely disable the button as "Saved".
  const isCleanActive = hasLoaded && !!activeSavedSearch && !isDirty;

  useEffect(() => {
    if (mode !== 'name') {
      return;
    }
    // Microtask defer so the Dialog content has time to mount before we
    // dispatch focus into it.
    queueMicrotask(() => {
      inputRef.current?.focus();
    });
  }, [mode]);

  // Resume a save interrupted by the login bounce. The claim is destructive
  // (read-and-remove), so with several SaveSearchButton instances mounted only
  // one opens the dialog.
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const pending = claimPendingSavedSearch();
    if (!pending) {
      return;
    }
    setResumedQuery(pending.query);
    setName(pending.name);
    setErrorMessage(null);
    setDuplicateOf(null);
    setMode('name');
  }, [isLoggedIn]);

  const openDialog = useCallback(() => {
    const suggestedName = formatSavedSearchName(query, locationLabel);
    if (!isLoggedIn) {
      stashPendingSavedSearch({ name: suggestedName, query });
      redirectToLogin();
      return;
    }
    setResumedQuery(null);
    setErrorMessage(null);
    setDuplicateOf(null);
    if (isDirty) {
      setMode('choose');
      return;
    }
    setName(suggestedName);
    setMode('name');
  }, [isLoggedIn, isDirty, query, locationLabel]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    setMode('closed');
  }, [submitting]);

  const handleSubmitNew = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    const saveQuery = resumedQuery ?? query;

    setSubmitting(true);
    setErrorMessage(null);

    // Warn once when an equivalent search already exists (same location +
    // filters). Await a settled list first so a fast submit before the initial
    // GetSavedSearches lands cannot skip duplicate detection. If the list still
    // has not loaded successfully, keep the dialog open rather than fail-open
    // against an unknown empty list. A second submit ("Save anyway") proceeds
    // past the warning.
    if (!duplicateOf) {
      let list = savedSearches;
      let listLoaded = hasLoaded;
      if (!listLoaded) {
        const result = await refetch();
        list = result.savedSearches;
        listLoaded = result.hasLoaded;
      }
      if (!listLoaded) {
        setErrorMessage(LIST_LOAD_ERROR);
        setSubmitting(false);
        return;
      }
      const existing = findMatchingSavedSearch(saveQuery, list);
      if (existing) {
        setDuplicateOf(existing);
        setSubmitting(false);
        return;
      }
    }

    const result = await add(toSavedSearchInput(saveQuery, trimmedName));
    setSubmitting(false);
    if (result.success) {
      trackEvent('cta-click', 'save-search-created', {
        savedSearchId: result.savedSearch?.id,
        market: saveQuery.market,
        zoneIdsCount: saveQuery.zoneIds?.length ?? 0,
        verticesCount: saveQuery.vertices?.length ?? 0,
      });
      toastManagerRef.current.add({ title: `Saved “${trimmedName}”`, variant: 'success' });
      setMode('closed');
      onSaved?.(result.savedSearch?.id ?? '');
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_SAVE_ERROR);
  }, [
    add,
    duplicateOf,
    hasLoaded,
    name,
    onSaved,
    query,
    refetch,
    resumedQuery,
    savedSearches,
    submitting,
    trackEvent,
  ]);

  const handleUpdateActive = useCallback(async () => {
    if (!activeSavedSearch || submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await update(
      activeSavedSearch.id,
      // The active record is the preservation source so fields Cosmos has no
      // UI for (max bed/bath, FHA, finite HOA cap) survive the update.
      toSavedSearchInput(query, activeSavedSearch.name, activeSavedSearch),
    );
    setSubmitting(false);
    if (result.success) {
      trackEvent('cta-click', 'save-search-updated', {
        savedSearchId: activeSavedSearch.id,
        market: query.market,
      });
      toastManagerRef.current.add({
        title: `Updated “${activeSavedSearch.name}”`,
        variant: 'success',
      });
      setMode('closed');
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_UPDATE_ERROR);
  }, [activeSavedSearch, query, submitting, trackEvent, update]);

  const handleChooseSaveAsNew = useCallback(() => {
    setName(formatSavedSearchName(query, locationLabel));
    setErrorMessage(null);
    setMode('name');
  }, [query, locationLabel]);

  const trimmedName = name.trim();
  const canSubmit = !submitting && trimmedName.length > 0;
  const triggerHint = validLocation ? undefined : NO_LOCATION_HINT;

  let submitLabel = duplicateOf ? 'Save anyway' : 'Save';
  if (submitting) {
    submitLabel = 'Saving…';
  }

  // The page state still matches the active saved search: show a quiet
  // confirmation instead of inviting a duplicate save.
  if (isCleanActive) {
    return (
      <Button
        analyticsName="save-search-button"
        size={size}
        variant="secondary"
        disabled
        aria-label={`Search saved as ${activeSavedSearch.name}`}
        className="max-md:min-w-0 max-md:flex-1"
      >
        <IconCheck size={18} aria-hidden />
        Saved
      </Button>
    );
  }

  const triggerLabel = isDirty ? 'Update search' : 'Save search';

  return (
    <>
      <Button
        analyticsName="save-search-button"
        onClick={openDialog}
        size={size}
        variant={variant}
        disabled={!validLocation}
        aria-label={validLocation ? triggerLabel : `${triggerLabel}. ${triggerHint}`}
        className="max-md:min-w-0 max-md:flex-1"
      >
        {triggerLabel}
      </Button>
      <UpdateSavedSearchDialog
        open={mode === 'choose'}
        submitting={submitting}
        activeSavedSearch={activeSavedSearch}
        errorMessage={errorMessage}
        onClose={handleClose}
        onSaveAsNew={handleChooseSaveAsNew}
        onUpdateActive={handleUpdateActive}
      />
      <NameSavedSearchDialog
        open={mode === 'name'}
        name={name}
        submitting={submitting}
        duplicateOf={duplicateOf}
        errorMessage={errorMessage}
        canSubmit={canSubmit}
        submitLabel={submitLabel}
        inputRef={setInputRef}
        nameInputId={nameInputId}
        onNameChange={setName}
        onClose={handleClose}
        onSubmit={handleSubmitNew}
      />
    </>
  );
};
